import { addDays, fromIsoDate, toIsoDate, type TaskPriority, type TaskStatus } from '@nexus/core';
import type { Task } from '@nexus/workspace';

export const todayIso = () => toIsoDate(new Date());

export const PRIORITY_ORDER: TaskPriority[] = ['highest', 'high', 'medium', 'low', 'lowest'];
export const PRIORITY_ICON: Record<TaskPriority, string> = {
  highest: '🔺',
  high: '⏫',
  medium: '🔼',
  low: '🔽',
  lowest: '⏬',
};
export const STATUSES: TaskStatus[] = ['todo', 'doing', 'waiting', 'done', 'cancelled'];

const rank = (p: TaskPriority | null) => (p ? PRIORITY_ORDER.indexOf(p) : 2.5);

/** Due date first (earliest, none last), then priority, then title. */
export function byDueThenPriority(a: Task, b: Task): number {
  const da = `${a.due ?? a.scheduled ?? '9999'} ${a.due ? (a.dueTime ?? '99:99') : ''}`;
  const db = `${b.due ?? b.scheduled ?? '9999'} ${b.due ? (b.dueTime ?? '99:99') : ''}`;
  return (
    da.localeCompare(db) || rank(a.priority) - rank(b.priority) || a.title.localeCompare(b.title)
  );
}

export type DueTone = 'overdue' | 'today' | 'soon' | 'later';

export function dueTone(due: string, today = todayIso()): DueTone {
  if (due < today) return 'overdue';
  if (due === today) return 'today';
  return due <= addDays(today, 6) ? 'soon' : 'later';
}

/** "Vandaag", "Morgen 14:00", "Gisteren", a weekday this week, else a short date; plus the time. */
export function formatDue(
  due: string,
  locale: string,
  t: (k: string) => string,
  today = todayIso(),
  time: string | null = null,
): string {
  const day = formatDay(due, locale, t, today);
  return time ? `${day} ${time}` : day;
}

function formatDay(due: string, locale: string, t: (k: string) => string, today: string): string {
  if (due === today) return t('tasks.today');
  if (due === addDays(today, 1)) return t('tasks.tomorrow');
  if (due === addDays(today, -1)) return t('tasks.yesterday');
  const d = fromIsoDate(due);
  if (due > today && due <= addDays(today, 6)) {
    return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d);
  }
  const sameYear = due.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
}

/** Title without #tags (they are shown as chips). */
export const displayTitle = (title: string) =>
  title
    .replace(/(^|\s)#[\p{L}\p{N}_\-/]+/gu, '$1')
    // A pasted link shows as "domain/path…" (the full link is in the tooltip and the page).
    .replace(/https?:\/\/(?:www\.)?([^\s/?#]+)([^\s?#]*)\S*/g, (_m, host: string, path: string) =>
      path.length > 1 ? `${host}${path.length > 24 ? `${path.slice(0, 24)}…` : path}` : host,
    )
    .replace(/\s+/g, ' ')
    .trim();
